// src/pages/CollectionsPage.tsx
//
// Lists the user's collection progress. Three sections, in order:
//   1. In progress  — collections with finds, not yet completed.
//      Sorted by last_find_at desc.
//   2. Completed    — collections fully found. Compact tiles
//      grouped under a single header; tapping opens the postcard.
//   3. Pending personal collections (seeds awaiting promotion) —
//      shown as soft-invite cards. The auto-promoter takes care
//      of high-confidence seeds; this section surfaces the mid-
//      confidence ones the user can opt in.
//
// Each card shows: cover (Wikipedia or user photo), title, tagline,
// progress, last-find time. Tap → opens CollectionDetailDrawer
// (hero image + two-row circle grid of items; tap a circle for the
// item detail card + walking directions).
//
// Scroll: the AppShell parent hides overflow and gives this page
// 100% of the main column height. We wrap the body in an
// overflowY:auto box so the list itself scrolls inside that. This
// matches FeedPage / CuratorLog / Profile.

import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  LinearProgress,
  Stack,
  Chip,
  Skeleton,
  Divider,
} from '@mui/material';
import { CheckCircle, AutoAwesome, MoreHoriz } from '@mui/icons-material';
import {
  collection as fsCollection,
  onSnapshot,
  query,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { useCollectionProgress } from '../hooks/useCollectionProgress';
import { useResolvedImageUrl } from '../feed/useResolvedImageUrl';
import { CollectionProgress } from '../types/collections';
import { useT } from '../i18n/useT';
import { CollectionDetailDrawer } from '../components/collections/CollectionDetailDrawer';

const formatRelative = (date: Date | null): string => {
  if (!date) return '';
  const diff = Date.now() - date.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
};

const CollectionTile: React.FC<{
  progress: CollectionProgress;
  onClick?: () => void;
}> = ({ progress, onClick }) => {
  const t = useT();
  const cover = useResolvedImageUrl(progress.collection_cover_image_url);
  const isCompleted = !!progress.completed_at;
  const isHidden = progress.hidden_total;
  const pct = isHidden
    ? 100
    : progress.total_count > 0
    ? Math.min(
        100,
        Math.round((progress.found_count / progress.total_count) * 100)
      )
    : 0;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: isCompleted ? 'success.light' : 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <CardActionArea onClick={onClick}>
        {cover && (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundImage: `url(${cover})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
            }}
          >
            {progress.collection_kind === 'personal' && (
              <Chip
                size="small"
                icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                label={t('Personal')}
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            )}
            {isCompleted && (
              <Chip
                size="small"
                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                label={t('Complete')}
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  bgcolor: 'success.main',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            )}
          </Box>
        )}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.25 }}
          >
            {progress.collection_title}
          </Typography>
          {progress.collection_tagline && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {progress.collection_tagline}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {isHidden
                ? t('{count} found', { count: progress.found_count })
                : t('{found} of {total}', {
                    found: progress.found_count,
                    total: progress.total_count,
                  })}
            </Typography>
            {progress.last_find_at && (
              <>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatRelative(progress.last_find_at)}
                </Typography>
              </>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={isCompleted ? 'success' : 'primary'}
            sx={{
              height: 6,
              borderRadius: 999,
              bgcolor: 'action.hover',
            }}
          />
        </Box>
      </CardActionArea>
    </Card>
  );
};

interface PersonalSeed {
  id: string;
  theme_label: string;
  rationale: string;
  confidence: number;
  proposed_at: Date | null;
  status: 'pending' | 'promoted' | 'declined' | 'expired';
}

/** Pending personal-collection seeds — the mid-confidence ones the
 *  auto-promoter didn't take. Surfaced as soft invites here so the
 *  user can opt in. */
const usePendingSeeds = (): PersonalSeed[] => {
  const [seeds, setSeeds] = useState<PersonalSeed[]>([]);
  const unsubRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    unsubRef.current?.();
    const ref = fsCollection(
      db,
      'users',
      user.uid,
      'personal_collection_seeds'
    );
    const q = query(ref, orderBy('proposed_at', 'desc'), limit(8));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: PersonalSeed[] = [];
        snap.forEach((d) => {
          const data = d.data() as any;
          if (data.status !== 'pending') return;
          list.push({
            id: d.id,
            theme_label: data.theme_label || '',
            rationale: data.rationale || '',
            confidence: data.confidence || 0,
            proposed_at:
              data.proposed_at instanceof Timestamp
                ? data.proposed_at.toDate()
                : null,
            status: data.status,
          });
        });
        setSeeds(list);
      },
      () => {}
    );
    unsubRef.current = unsub;
    return () => unsubRef.current?.();
  }, []);
  return seeds;
};

const SeedCard: React.FC<{ seed: PersonalSeed }> = ({ seed }) => {
  const t = useT();
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px dashed',
        borderColor: 'secondary.light',
        bgcolor: 'background.paper',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <AutoAwesome sx={{ fontSize: 18, color: 'secondary.main' }} />
        <Typography variant="overline" sx={{ letterSpacing: 1 }}>
          {t('Idea for you')}
        </Typography>
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {seed.theme_label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {seed.rationale}
      </Typography>
    </Card>
  );
};

export const CollectionsPage: React.FC = () => {
  const t = useT();
  const { progress, isLoading } = useCollectionProgress();
  const seeds = usePendingSeeds();
  // Currently-open collection detail drawer. The drawer fetches
  // its own items + finds via useCollectionDetail.
  const [selected, setSelected] = useState<CollectionProgress | null>(null);

  const active = useMemo(
    () => progress.filter((p) => !p.completed_at && !p.dismissed),
    [progress]
  );
  const completed = useMemo(
    () => progress.filter((p) => !!p.completed_at && !p.dismissed),
    [progress]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          p: 2,
          pb: 12,
          pt: 'calc(var(--app-header-h) + 16px)',
        }}
      >
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={200} />
          ))}
        </Stack>
      </Box>
    );
  }

  // Empty state — for the user who hasn't earned any finds yet
  if (active.length === 0 && completed.length === 0 && seeds.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          py: 6,
          pb: 12,
          pt: 'calc(var(--app-header-h) + 48px)',
          minHeight: '60dvh',
        }}
      >
        <AutoAwesome sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t('Your collections live here')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360 }}>
          {t(
            "Take photos of landmarks and the app quietly fits them into themed collections — Bernini's Rome, hidden corners of Trastevere, that kind of thing. They show up here as you find them."
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Scroll container — the parent AppShell column hides
        // overflow so we own scrolling here. height:100% + overflowY:auto
        // matches the FeedPage / CuratorLog convention.
        height: '100%',
        overflowY: 'auto',
        p: 2,
        pb: 12,
        pt: 'calc(var(--app-header-h) + 16px)',
      }}
    >
      {active.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('In progress')}
          </Typography>
          <Stack
            spacing={2}
            sx={{ mb: seeds.length > 0 || completed.length > 0 ? 3 : 0 }}
          >
            {active.map((p) => (
              <CollectionTile
                key={p.id}
                progress={p}
                onClick={() => setSelected(p)}
              />
            ))}
          </Stack>
        </>
      )}

      {seeds.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('Ideas for you')}
          </Typography>
          <Stack spacing={2} sx={{ mb: completed.length > 0 ? 3 : 0 }}>
            {seeds.map((s) => (
              <SeedCard key={s.id} seed={s} />
            ))}
          </Stack>
        </>
      )}

      {completed.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Chip
              icon={<CheckCircle sx={{ fontSize: 14 }} />}
              label={t('{count} complete', { count: completed.length })}
              size="small"
              color="success"
              variant="outlined"
              sx={{ '& .MuiChip-icon': { color: 'success.main' } }}
            />
          </Divider>
          <Stack spacing={2}>
            {completed.map((p) => (
              <CollectionTile
                key={p.id}
                progress={p}
                onClick={() => setSelected(p)}
              />
            ))}
          </Stack>
        </>
      )}

      {/* Detail drawer for whichever collection is tapped */}
      <CollectionDetailDrawer
        progress={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};

export default CollectionsPage;
